import {FC, useMemo} from 'react';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';

import Message from '@/components/message';
import SectionTitle from '@/components/section-title';
import Table from '@/components/table';
import {TableData} from '@/components/table/table';
import {UserProfile} from '@/repositories/beneva-api/endpoints/users';

type Props = Pick<UserProfile, 'delegatedAdvisors' | 'orphanCodes' | 'advisorRole'>;

export const DelegatedAccessSection: FC<Props> = ({advisorRole, delegatedAdvisors = [], orphanCodes = []}) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  const rows = useMemo((): TableData | undefined => {
    if (isEmpty(delegatedAdvisors)) return;

    return {
      headers: (['lastname', 'firstname', 'email', 'codes'] as const).map(header =>
        t(`delegated-access.table.${header}`),
      ),
      body: delegatedAdvisors.map(({advisorId, firstName, lastName, email, codes}) => ({
        dataTestid: `delegated-advisor-${advisorId}`,
        columns: [firstName, lastName, email, codes.join(', ')],
      })),
    };
  }, [delegatedAdvisors, t]);

  const orphanCodesList = useMemo(() => orphanCodes.join(', '), [orphanCodes]);

  return (
    <div className="flex flex-col">
      <div>
        <hr />
        <SectionTitle data-testid="delegated-access-title">{t(`delegated-access.title.${advisorRole}`)}</SectionTitle>
        {rows && <Table data={rows} dataTestid="delegated-access-table" />}
      </div>

      {!rows && !orphanCodesList && (
        <Message
          dataTestid="no-delegated-advisors-or-orphan-codes-message"
          text={t('delegated-access.no-delegated-advisors-or-orphan-codes')}
        />
      )}

      {!!orphanCodesList && (
        <div className="mt-10 flex flex-col gap-2">
          <h4 className="font-medium">{t('delegated-access.orphan-codes.title')}</h4>
          <p>{orphanCodesList}</p>
          <p className="text-sm">{t('delegated-access.orphan-codes.description')}</p>
        </div>
      )}
    </div>
  );
};
